import classNames from 'classnames';
import MultiPicker from 'components/foundation/pickers/MultiPicker/MultiPicker';
import { useCertificationChecker } from 'containers/certification-status-report/hooks/useCertificationChecker';
import { useOpenable } from 'hooks';
import { useRef, useState } from 'react';
import { ClassName } from 'types/ClassName';
import { useFeatureFlags } from 'utils/features';
import EquipmentAddOnDialog from '../EquipmentAddOnDialog/EquipmentAddOnDialog';
import EquipmentPicker from '../EquipmentPicker/EquipmentPicker';
import './EquipmentAddOnPicker.scss';
import { isNil } from 'lodash';

export interface IEquipmentAddOnPickerProps {
  className?: ClassName;
  ids: string[];
  filteredIds?: string[];
  placeholder?: string;
  memberIds?: string[];
  onChange: (ids: string[]) => void;
  onCanSubmitChanged?: (canSubmit: boolean) => void;
}

const EquipmentAddOnPicker = (props: IEquipmentAddOnPickerProps) => {
  const { className, ids, filteredIds, placeholder, memberIds, onChange, onCanSubmitChanged } = props;

  const equipmentAddOnOpenable = useOpenable();
  const { getMissingEquipmentCertifications } = useCertificationChecker();
  const isCertificationStatusEnabled = useFeatureFlags('CERTIFICATIONS');

  const [certCheckMap, setCertCheckMap] = useState<Record<string, boolean>>({});
  const idsWithFilter = filteredIds ? ids.filter((id) => !filteredIds.some((filteredId) => filteredId === id)) : ids;
  const lastSubmitStatus = useRef<boolean | null>(null);

  const evaluateCanSubmit = (selectedIds: string[], nextMap: Record<string, boolean>) => {
    const combinedIds = filteredIds ? Array.from(new Set([...selectedIds, ...filteredIds])) : selectedIds;
    const allClear = combinedIds.every((id) => isNil(nextMap[id]) || nextMap[id] === true);
    if (lastSubmitStatus.current !== allClear) {
      lastSubmitStatus.current = allClear;
      onCanSubmitChanged?.(allClear);
    }
  };

  const handleOnChange = (updatedIds: string[]) => {
    if (isCertificationStatusEnabled && memberIds?.length) {
      const idsToKeep = filteredIds ? [...updatedIds, ...filteredIds] : updatedIds;
      const cleanedMap = Object.fromEntries(Object.entries(certCheckMap).filter(([id]) => idsToKeep.includes(id)));
      setCertCheckMap(cleanedMap);
      evaluateCanSubmit(updatedIds, cleanedMap);
    }
    onChange(updatedIds);
  };

  const handleAddOnDialogSubmit = async (equipmentIds: string[]) => {
    equipmentAddOnOpenable.close();
    const updatedMap: Record<string, boolean> = { ...certCheckMap };

    if (isCertificationStatusEnabled && memberIds?.length) {
      const idsToCheck = filteredIds ? [...equipmentIds, ...filteredIds] : equipmentIds;
      for (const id of idsToCheck) {
        const results = await getMissingEquipmentCertifications(memberIds, [id]);
        const hasMissing = results.some((m) => m.missingCerts.length > 0);
        updatedMap[id] = !hasMissing;
      }
      setCertCheckMap(updatedMap);
      evaluateCanSubmit(equipmentIds, updatedMap);
    } else {
      setCertCheckMap(updatedMap);
    }

    onChange(equipmentIds);
  };

  const classes = classNames('equipment-add-on-picker', className);

  const renderItem = (value: string | null, index: number) => {
    const onEquipmentSelect = (id: string | null) => {
      if (index === 0 || index < ids.length) {
        if (id !== null) {
          const newValues = [...ids];
          newValues[index] = id;
          handleOnChange(newValues);
        } else {
          handleOnChange(ids.filter((v, i) => i !== index));
        }
      }
    };

    return (
      <EquipmentPicker
        className={index !== 0 ? 'mt-4' : ''}
        filteredIds={filteredIds ? ids.concat(filteredIds) : ids}
        key={value ?? 'null'}
        value={value}
        onSelect={(selectedId, canSubmit = true) => {
          onEquipmentSelect(selectedId);
          if (selectedId) {
            const updatedMap = { ...certCheckMap, [selectedId]: canSubmit };
            setCertCheckMap(updatedMap);
            if (isCertificationStatusEnabled && memberIds?.length) {
              const selectedIds =
                index === 0 || index < ids.length ? [...ids.slice(0, index), selectedId, ...ids.slice(index + 1)] : ids;
              evaluateCanSubmit(selectedIds, updatedMap);
            }
          }
        }}
        placeholder={placeholder}
        filterAddOns={false}
        memberIds={memberIds}
        onClear={() => {
          if (value) {
            const { [value]: _, ...rest } = certCheckMap;
            setCertCheckMap(rest);
            if (isCertificationStatusEnabled && memberIds?.length) {
              const selectedIds = ids.filter((v, i) => i !== index);
              evaluateCanSubmit(selectedIds, rest);
            }
          }
        }}
        showMissingCertWarning={value !== null ? certCheckMap[value] === false : false}
      />
    );
  };

  return (
    <div className={classes}>
      <EquipmentAddOnDialog
        isOpen={equipmentAddOnOpenable.isOpen}
        onClose={equipmentAddOnOpenable.close}
        onSubmit={handleAddOnDialogSubmit}
        currentSelections={idsWithFilter}
        filteredIds={filteredIds}
        memberIds={memberIds}
      />
      <MultiPicker
        onAddClick={equipmentAddOnOpenable.open}
        limit={20}
        renderItem={renderItem}
        values={idsWithFilter && idsWithFilter.length ? idsWithFilter : [null]}
        className={'add-on-multipicker'}
      />
    </div>
  );
};

export default EquipmentAddOnPicker;
